import { c as _c } from "react/compiler-runtime";
import { useState, type ReactNode } from "react";
import { DateTime } from "luxon";
import { ChevronDown, CircleCheck, CircleDashed, CircleDollarSign, CircleX, ExternalLink, GitPullRequestArrow, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@repo/blacksmith-ui/cn";
import { KEEP_GOING_ROUNDS, LoopLimitStrip } from "@/components/codesmith/LoopLimitStrip";
import { ChatCard } from "./ChatCard";
import { Button } from "@repo/blacksmith-ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { HoverTickerText } from "@/components/ui/hover-ticker-text";
import { ProofIcon } from "@/components/codesmith/proof/proofIcon";
import { AutofixHammerIcon } from "./AutofixHammerIcon";
import { PR_STATUS_PRESENTATION } from "./managedPrDisplay";
import { MESSAGE_INSET_FOCUS_BORDER_CLASS } from "./messageFocusStyles";
import { useNowTick } from "./subtaskTicker";
import { useWorkspaceTabsContext } from "./workspace-pane/WorkspaceTabsContext";
import type { ContinueProofRoundsHandler, ContinueReviewRoundsHandler, ManagedPr, PrPreviewLink, WorkspaceArtifact, WorkspaceArtifactId } from "./types";
import { useWorkspacePaneEnabled } from "./workspace-pane/workspacePaneFlag";
export const PR_POPOVER_CLASS = "max-w-[calc(100vw-2rem)] rounded-lg border-black/[0.08] p-1 shadow-[0px_10px_20px_rgba(0,0,0,0.2)] dark:border-white/[0.08]";

/** Strip markdown formatting and HTML comments for a plain-text preview. */
export function stripMarkdownForPreview(text: string): string {
  return text.replace(/<!--[\s\S]*?-->/g, "").replace(/^#{1,6}\s+/gm, "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1").replace(/`([^`]+)`/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/^\s*[-*]\s+/gm, "").replace(/\n{2,}/g, " ").replace(/\n/g, " ").trim();
}

/**
 * Whether an open PR's check rollup is green: every check on the head
 * completed successfully and none are in progress. Drives the "Ready"
 * presentation of the open status across the sidebar badges and PR cards.
 */
export function isPrReady(pr: Pick<ManagedPr, "status" | "status_check_state">): boolean {
  return pr.status === "open" && pr.status_check_state === "success";
}

/** Simplified CI state for display, derived from the check rollup. */
export type PrCiStatus = "passing" | "running" | "failing";

/**
 * Current CI state of the PR head, from the backend's persisted
 * status-check rollup. Null when there is nothing meaningful to show:
 * the PR is merged/closed (the snapshot is stale) or no snapshot has
 * landed yet / the head has no checks.
 */
export function getPrCiStatus(pr: Pick<ManagedPr, "status" | "status_check_state">): PrCiStatus | null {
  if (pr.status === "merged" || pr.status === "closed") return null;
  switch (pr.status_check_state) {
    case "failure":
    case "error":
      return "failing";
    case "pending":
    case "expected":
      return "running";
    case "success":
      return "passing";
    default:
      return null;
  }
}

/**
 * 12px status icon. On open PRs the CI state takes over the icon: green
 * check when the rollup is green (`ready`), red X when failing, amber
 * dashed circle while running. Other statuses always show their own icon.
 */
export const getPrStatusIcon = (status: ManagedPr["status"], ready = false, ci: PrCiStatus | null = null) => {
  if (status === "open") {
    if (ready) return <CircleCheck className="size-3 text-ds-green" />;
    if (ci === "failing") return <CircleX className="size-3 text-ds-red" />;
    if (ci === "running") return <CircleDashed className="size-3 animate-spin [animation-duration:2s] text-amber-600 dark:text-amber-400" />;
  }
  const {
    Icon,
    colorClass
  } = PR_STATUS_PRESENTATION[status] ?? PR_STATUS_PRESENTATION.open;
  return <Icon className={cn("size-3", colorClass)} />;
};

/**
 * Status label text + Tailwind color class. Open PRs read as their CI
 * state when one is known ("Ready" / "CI failing" / "CI running") and
 * plain "Open" otherwise; other statuses always show their own label.
 */
export const getPrStatusLabel = (status: ManagedPr["status"], ready = false, ci: PrCiStatus | null = null): {
  label: string;
  colorClass: string;
} => {
  if (status === "open") {
    if (ready) return {
      label: "Ready",
      colorClass: "text-ds-green"
    };
    if (ci === "failing") return {
      label: "CI failing",
      colorClass: "text-ds-red"
    };
    if (ci === "running") return {
      label: "CI running",
      colorClass: "text-amber-600 dark:text-amber-400"
    };
  }
  const {
    label,
    colorClass
  } = PR_STATUS_PRESENTATION[status] ?? PR_STATUS_PRESENTATION.open;
  return {
    label,
    colorClass
  };
};
export const PR_STATUS_ORDER: ManagedPr["status"][] = ["open", "draft", "merged", "closed"];
const statusPriority: Record<ManagedPr["status"], number> = {
  open: 0,
  draft: 1,
  merged: 2,
  closed: 3
};

/**
 * Sort PRs by status priority (open → draft → merged → closed), then newest
 * first within the same status.  Returns a new array — does not mutate input.
 */
export function sortManagedPrs<T extends {
  status: ManagedPr["status"];
  created_at?: string;
}>(prs: readonly T[]): T[] {
  return [...prs].sort((a, b) => {
    const sp = statusPriority[a.status] - statusPriority[b.status];
    if (sp !== 0) return sp;
    if (a.created_at && b.created_at) {
      return b.created_at.localeCompare(a.created_at);
    }
    return 0;
  });
}

/** Compact relative-time string (e.g. "3h", "12 min"). */
const compactRelativeTime = (iso: string, nowMs: number = Date.now()): string => {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return "";
  const diffSec = Math.floor((nowMs - dt.toMillis()) / 1000);
  if (diffSec < 60) return `${diffSec}s`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
  if (diffSec < 2592000) return `${Math.floor(diffSec / 86400)}d`;
  return `${Math.floor(diffSec / 2592000)}mo`;
};
export function AutofixToggle({
  pr,
  onToggle,
  tabIndex,
  label = "Autofix PR",
  disabled = false,
  compact = false
}: {
  pr: Pick<ManagedPr, "id" | "autofix_enabled">;
  onToggle?: (prId: string, enabled: boolean) => void | Promise<void>;
  tabIndex?: number;
  label?: string;
  disabled?: boolean;
  compact?: boolean;
}) {
  // Target value of the in-flight toggle; shown instead of refetched data so
  // the pill doesn't flicker while the request settles.
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const isPending = pendingValue !== null;
  const displayEnabled = pendingValue ?? pr.autofix_enabled;
  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending || disabled) return;
    const nextValue = !pr.autofix_enabled;
    setPendingValue(nextValue);
    try {
      await onToggle?.(pr.id, nextValue);
    } finally {
      setPendingValue(null);
    }
  };
  return <div className={cn("flex items-center transition-opacity", compact ? "gap-0.5" : "gap-2", (isPending || disabled) && "opacity-50")}>
      <span className={cn("whitespace-nowrap", compact ? "type-body-xs-regular text-[#727272] dark:text-neutral-500" : ["text-xs font-medium leading-[18px] tracking-[-0.1px]", displayEnabled ? "text-[#18191A] dark:text-neutral-100" : "text-[#727272] dark:text-neutral-500"])}>
        {label}
      </span>
      <button onClick={handleClick} disabled={isPending || disabled} tabIndex={tabIndex} className={cn("relative w-[18px] h-[11px] rounded-full transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-0", compact && "focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]", isPending ? "cursor-default" : "cursor-pointer", displayEnabled ? "bg-[#313435] dark:bg-neutral-200" : "bg-[#BBBBBB] dark:bg-neutral-600")} aria-label={`Toggle autofix ${displayEnabled ? "off" : "on"}`}>
        <span className={cn("absolute top-[1px] size-[9px] rounded-full bg-[#F5F5F5] dark:bg-neutral-800 transition-transform", displayEnabled ? "left-[8px]" : "left-[1px]")} />
      </button>
    </div>;
}

/**
 * Whether the PR card should surface the autofix spend pause: autofix hit
 * the per-PR limit and nobody has acknowledged it, on a PR autofix would
 * otherwise still be working.
 */
export function isAutofixSpendLimitPaused(pr: Pick<ManagedPr, "status" | "autofix_enabled" | "autofix_spend_limit_reached">): boolean {
  return pr.autofix_spend_limit_reached === true && pr.autofix_enabled && (pr.status === "open" || pr.status === "draft");
}
function PrRoundsBanner({
  pr,
  kind,
  onContinue,
  className,
  tabIndex = -1
}: {
  pr: ManagedPr;
  kind: "review" | "proof";
  onContinue?: ContinueReviewRoundsHandler | ContinueProofRoundsHandler;
  className?: string;
  tabIndex?: number;
}) {
  const [isPending, setIsPending] = useState(false);
  const rounds = kind === "review" ? pr.review_rounds : pr.proof_rounds;
  const waitingSessionId = kind === "review" ? pr.review_rounds?.waiting_review_session_id : pr.proof_rounds?.waiting_proof_session_id;
  if (!rounds || !waitingSessionId || kind === "review" && (!pr.autofix_enabled || pr.autofix_spend_limit_reached || pr.status !== "open" && pr.status !== "draft") || kind === "proof" && (!onContinue || pr.status !== "open" && pr.status !== "draft")) {
    return null;
  }
  return <LoopLimitStrip className={cn("mt-1 bg-[var(--color-surface-inset)] px-2 py-1.5", className)} continued={false} continuedHeadline={`Keeping going: up to ${KEEP_GOING_ROUNDS} more rounds`} detail={kind === "review" ? "A review is waiting." : "A Proof is waiting."} headline={`${kind === "review" ? "Review" : "Proof"} paused after ${rounds.max} rounds`} onKeepGoing={onContinue ? async event => {
    event.preventDefault();
    event.stopPropagation();
    if (isPending || !waitingSessionId) return;
    setIsPending(true);
    try {
      await onContinue(pr.id, KEEP_GOING_ROUNDS, waitingSessionId);
    } finally {
      setIsPending(false);
    }
  } : undefined} pending={isPending} tabIndex={tabIndex} testId={`${kind}-round-cap-banner`} onClick={event_0 => {
    event_0.preventDefault();
    event_0.stopPropagation();
  }} />;
}
function AutofixSpendLimitBanner({
  pr,
  onContinue,
  className,
  tabIndex = -1
}: {
  pr: Pick<ManagedPr, "id" | "autofix_spend_usd" | "autofix_spend_limit_usd">;
  onContinue?: (prId: string) => void | Promise<void>;
  className?: string;
  tabIndex?: number;
}) {
  const [isPending, setIsPending] = useState(false);
  const limitLabel = pr.autofix_spend_limit_usd != null && pr.autofix_spend_limit_usd > 0 ? `$${Math.round(pr.autofix_spend_limit_usd).toLocaleString()}` : "configured";
  const spendLabel = pr.autofix_spend_usd != null && pr.autofix_spend_usd > 0 ? `$${pr.autofix_spend_usd.toFixed(2)}` : null;
  const handleContinue = async (e: React.MouseEvent) => {
    // The card is an anchor to the PR; Continue must not navigate.
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    setIsPending(true);
    try {
      await onContinue?.(pr.id);
    } finally {
      setIsPending(false);
    }
  };
  return <div data-testid="autofix-spend-limit-banner" className={cn("mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 dark:border-amber-800/50 dark:bg-amber-950/30", className)}>
      <div className="flex min-w-0 items-start gap-1.5">
        <CircleDollarSign className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        <div className="min-w-0">
          <p className="type-body-xs-medium text-amber-900 dark:text-amber-200">
            Autofix paused at its {limitLabel} spend limit
          </p>
          <p className="type-body-xs-regular text-amber-700 dark:text-amber-400/90">
            {spendLabel ? `${spendLabel} used. ` : ""}Continue to let autofix
            keep working on this PR.
          </p>
        </div>
      </div>
      {onContinue && <Button variant="secondary" size="xs" tabIndex={tabIndex} className="shrink-0" onClick={handleContinue} disabled={isPending}>
          {isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
          Continue
        </Button>}
    </div>;
}
interface PrCardBaseProps {
  pr: ManagedPr;
  onToggleAutofix?: (prId: string, enabled: boolean) => void | Promise<void>;
  autofixLabel?: string;
  onContinueAutofixSpendLimit?: (prId: string) => void | Promise<void>;
  onContinueReviewRounds?: ContinueReviewRoundsHandler;
  onContinueProofRounds?: ContinueProofRoundsHandler;
}
interface PrCardMenuProps extends PrCardBaseProps {
  /** Compact row for dropdown / sidebar lists. */
  variant?: "menu";
}
interface PrCardSummaryProps extends PrCardBaseProps {
  /** Three-line card in the workspace Summary widget. */
  variant: "summary";
}
interface PrCardChatProps extends PrCardBaseProps {
  /** Inline card shown in the chat timeline after a PR is created. */
  variant: "chat";
}
function VercelTriangle(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("-translate-y-[7.5%]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M37.59.25l36.95 64H.64l36.95-64z" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <svg viewBox="0 0 76 65" fill="currentColor" className={t1}>{t2}</svg>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** All preview/URL artifacts on a PR, falling back to the single preview_url. */
export function getPrPreviewLinks(pr: Pick<ManagedPr, "preview_url" | "preview_links">): PrPreviewLink[] {
  if (pr.preview_links?.length) return pr.preview_links;
  if (pr.preview_url) return [{
    label: "Preview",
    url: pr.preview_url
  }];
  return [];
}

/**
 * Preview links safe to open programmatically (⌥P). Explicit preview_links
 * are directly openable artifacts, but the fallback preview_url follows the
 * preview button's availability rules: a registered URL serves nothing
 * useful mid-initial-build or when no build ever succeeded.
 */
export function getOpenablePrPreviewLinks(pr: Pick<ManagedPr, "preview_url" | "preview_links" | "preview_build_status" | "preview_built_at" | "preview_build_status_changed_at">): PrPreviewLink[] {
  if (pr.preview_links?.length) return pr.preview_links;
  const buttonState = getPreviewButtonState(pr);
  return buttonState && !buttonState.disabled ? getPrPreviewLinks(pr) : [];
}
const linkHostname = (url: string): string | null => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};
export interface PreviewButtonState {
  /** Nothing to open: never built, or no URL registered yet. */
  disabled: boolean;
  icon: "vercel" | "building" | "error";
  tooltip?: string;
}

/**
 * Preview button presentation from the PR's build state machine:
 * initial build -> disabled + spinner; built -> plain button; rebuild ->
 * clickable (last good build) + spinner; failed -> error icon, clickable
 * only when a build ever succeeded. Null hides the button entirely.
 */
export function getPreviewButtonState(pr: Pick<ManagedPr, "preview_url" | "preview_build_status" | "preview_built_at" | "preview_build_status_changed_at">,
// Explicit clock input: with React Compiler memoizing on `pr`, an implicit
// Date.now() read would freeze the building tooltip's elapsed time.
nowMs: number = Date.now()): PreviewButtonState | null {
  const status = pr.preview_build_status ?? null;
  // No build state observed: legacy rows and non-Vercel previews keep the
  // plain always-clickable button when a URL is registered.
  if (status === null) {
    return pr.preview_url ? {
      disabled: false,
      icon: "vercel"
    } : null;
  }
  if (status === "built") {
    return {
      disabled: !pr.preview_url,
      icon: "vercel"
    };
  }

  // Openable only when a build ever succeeded: the branch-scoped URL keeps
  // serving the last good build through rebuilds and failures. Null
  // preview_built_at means no useful preview exists; rows predating the
  // tracking were backfilled from their registered URL (see the
  // backfill_preview_built_at_on_managed_prs migration).
  const openable = !!pr.preview_url && pr.preview_built_at != null;
  if (status === "building") {
    // The backend moves preview_build_status_changed_at only on status
    // transitions, so during "building" it is when the build started.
    const buildingFor = pr.preview_build_status_changed_at ? compactRelativeTime(pr.preview_build_status_changed_at, nowMs) : "";
    const base = openable ? "Rebuilding preview" : "Preview environment is being built";
    return {
      disabled: !openable,
      icon: "building",
      tooltip: buildingFor ? `${base} (${buildingFor})` : base
    };
  }
  return {
    disabled: !openable,
    icon: "error",
    tooltip: "Latest version failed to build"
  };
}

// Every Button state reads --color-border, so swapping it covers them all.
const PREVIEW_BUTTON_BORDER_CLASS = "[--color-border:var(--color-border-inset)]";
function PrPreviewButton(t0) {
  const $ = _c(28);
  const {
    pr,
    iconClassName,
    gapClassName,
    tabIndex: t1
  } = t0;
  const tabIndex = t1 === undefined ? -1 : t1;
  const now = useNowTick(pr.preview_build_status === "building" && !!pr.preview_build_status_changed_at);
  let icon;
  let state;
  let t2;
  let t3;
  if ($[0] !== iconClassName || $[1] !== now || $[2] !== pr) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      state = getPreviewButtonState(pr, now);
      if (!state) {
        t3 = null;
        break bb0;
      }
      icon = state.icon === "building" ? <Loader2 className={cn(iconClassName, "animate-spin")} /> : state.icon === "error" ? <TriangleAlert className={cn(iconClassName, "text-ds-red")} /> : <VercelTriangle className={iconClassName} />;
      t2 = cn("inline-flex shrink-0", state.disabled && "cursor-default");
    }
    $[0] = iconClassName;
    $[1] = now;
    $[2] = pr;
    $[3] = icon;
    $[4] = state;
    $[5] = t2;
    $[6] = t3;
  } else {
    icon = $[3];
    state = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const t4 = state.disabled;
  let t5;
  if ($[7] !== pr.preview_url || $[8] !== state.disabled) {
    t5 = () => {
      if (state.disabled || !pr.preview_url) {
        return;
      }
      window.open(pr.preview_url, "_blank", "noopener,noreferrer");
    };
    $[7] = pr.preview_url;
    $[8] = state.disabled;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== gapClassName) {
    t6 = cn("flex shrink-0 items-center", gapClassName, PREVIEW_BUTTON_BORDER_CLASS);
    $[10] = gapClassName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== icon || $[13] !== state.disabled || $[14] !== t5 || $[15] !== t6 || $[16] !== tabIndex) {
    t7 = <Button variant="secondary" size="xs" tabIndex={tabIndex} aria-label="Open preview environment" disabled={t4} onClick={t5} className={t6}>{icon}Preview</Button>;
    $[12] = icon;
    $[13] = state.disabled;
    $[14] = t5;
    $[15] = t6;
    $[16] = tabIndex;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t2 || $[19] !== t7) {
    t8 = <span className={t2} onClick={_temp}>{t7}</span>;
    $[18] = t2;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const button = t8;
  if (!state.tooltip) {
    return button;
  }
  let t9;
  if ($[21] !== button) {
    t9 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[21] = button;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== state.tooltip) {
    t10 = <TooltipContent>{state.tooltip}</TooltipContent>;
    $[23] = state.tooltip;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <Tooltip>{t9}{t10}</Tooltip>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}

/**
 * Preview artifacts in the menu bottom row. Explicit artifacts render a
 * plain "Preview" button (one) or a dropdown with a count badge (2+);
 * without them the single preview_url button carries the Vercel build
 * state machine via PrPreviewButton. Lives inside the card anchor, so
 * every click must stop propagation and prevent navigation.
 */
function _temp(e) {
  e.preventDefault();
  e.stopPropagation();
}
function PrPreviewLinksButton(t0) {
  const $ = _c(27);
  const {
    pr,
    tabIndex: t1
  } = t0;
  const tabIndex = t1 === undefined ? -1 : t1;
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== pr || $[1] !== tabIndex) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const links = getPrPreviewLinks(pr);
      if (!pr.preview_links?.length) {
        t7 = <PrPreviewButton pr={pr} iconClassName="size-2" gapClassName="gap-1" tabIndex={tabIndex} />;
        break bb0;
      }
      if (links.length === 1) {
        let t8;
        let t9;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = cn("flex shrink-0 items-center gap-1", PREVIEW_BUTTON_BORDER_CLASS);
          t9 = <VercelTriangle className="size-2" />;
          $[10] = t8;
          $[11] = t9;
        } else {
          t8 = $[10];
          t9 = $[11];
        }
        t7 = <Button variant="secondary" size="xs" tabIndex={tabIndex} aria-label="Open preview environment" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          window.open(links[0].url, "_blank", "noopener,noreferrer");
        }} className={t8}>{t9}Preview</Button>;
        break bb0;
      }
      T1 = DropdownMenu;
      let t8;
      let t9;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = cn("flex shrink-0 items-center gap-1", PREVIEW_BUTTON_BORDER_CLASS);
        t9 = <VercelTriangle className="size-2" />;
        $[12] = t8;
        $[13] = t9;
      } else {
        t8 = $[12];
        t9 = $[13];
      }
      let t10;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <ChevronDown className="size-2.5 text-[#727272] dark:text-neutral-400" />;
        $[14] = t10;
      } else {
        t10 = $[14];
      }
      const t11 = <Button variant="secondary" size="xs" tabIndex={tabIndex} aria-label={`Open one of ${links.length} previews`} onClick={_temp2} className={t8}>{t9}Previews<span className="rounded-full bg-black/[0.07] px-1 text-[10px] leading-[14px] tabular-nums text-[#727272] dark:bg-white/[0.1] dark:text-neutral-400">{links.length}</span>{t10}</Button>;
      if ($[15] !== t11) {
        t6 = <DropdownMenuTrigger asChild={true}>{t11}</DropdownMenuTrigger>;
        $[15] = t11;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      T0 = DropdownMenuContent;
      t2 = "start";
      t3 = "w-56";
      t4 = _temp3;
      t5 = links.map(_temp4);
    }
    $[0] = pr;
    $[1] = tabIndex;
    $[2] = T0;
    $[3] = T1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    T0 = $[2];
    T1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[17] !== T0 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t8 = <T0 align={t2} className={t3} onClick={t4}>{t5}</T0>;
    $[17] = T0;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== T1 || $[24] !== t6 || $[25] !== t8) {
    t9 = <T1>{t6}{t8}</T1>;
    $[23] = T1;
    $[24] = t6;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}

/** The PR's most recent Proof report artifact, e.g. `acme/web#42` → `"proof-report:ses_2"`. */
function _temp4(link) {
  const hostname = linkHostname(link.url);
  return <DropdownMenuItem key={link.url} onClick={e_2 => {
    e_2.stopPropagation();
    window.open(link.url, "_blank", "noopener,noreferrer");
  }} className="flex items-center gap-2"><span className="min-w-0 flex-1"><span className="block truncate text-xs font-medium leading-4">{link.label}</span>{hostname && <span className="block truncate text-[11px] leading-4 text-neutral-400 dark:text-neutral-500">{hostname}</span>}</span><ExternalLink className="size-3 shrink-0 text-neutral-400 dark:text-neutral-500" /></DropdownMenuItem>;
}
function _temp3(e_1) {
  e_1.preventDefault();
  e_1.stopPropagation();
}
function _temp2(e_0) {
  e_0.preventDefault();
  e_0.stopPropagation();
}
function latestPrProofArtifactId(artifacts: Iterable<WorkspaceArtifact>, pr: Pick<ManagedPr, "repository" | "pr_number">): WorkspaceArtifactId | null {
  const repository = pr.repository.toLowerCase();
  let latest: WorkspaceArtifact | null = null;
  for (const artifact of artifacts) {
    if (artifact.kind !== "proof_report" || artifact.prNumber !== pr.pr_number || artifact.repository?.toLowerCase() !== repository) {
      continue;
    }
    if (!latest || (artifact.createdAt?.getTime() ?? 0) > (latest.createdAt?.getTime() ?? 0)) {
      latest = artifact;
    }
  }
  return latest?.id ?? null;
}

/** Opens the PR's latest Proof in the workspace pane; null when there is none to open. */
function usePrProofOpener(pr) {
  const $ = _c(6);
  const workspace = useWorkspaceTabsContext();
  const paneEnabled = useWorkspacePaneEnabled();
  if (!workspace || !paneEnabled) {
    return null;
  }
  let t0;
  if ($[0] !== pr || $[1] !== workspace.artifacts) {
    t0 = latestPrProofArtifactId(workspace.artifacts.values(), pr);
    $[0] = pr;
    $[1] = workspace.artifacts;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const artifactId = t0;
  if (!artifactId) {
    return null;
  }
  let t1;
  if ($[3] !== artifactId || $[4] !== workspace) {
    t1 = () => workspace.openArtifact(artifactId);
    $[3] = artifactId;
    $[4] = workspace;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function PrProofButton(t0) {
  const $ = _c(10);
  const {
    onOpen,
    tabIndex: t1
  } = t0;
  const tabIndex = t1 === undefined ? -1 : t1;
  let t2;
  if ($[0] !== onOpen) {
    t2 = e => {
      e.preventDefault();
      e.stopPropagation();
      onOpen();
    };
    $[0] = onOpen;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("flex shrink-0 items-center !w-[24px] !px-0", PREVIEW_BUTTON_BORDER_CLASS);
    t4 = <ProofIcon className="size-3" />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== tabIndex) {
    t5 = <TooltipTrigger asChild={true}><Button variant="secondary" size="xs" tabIndex={tabIndex} aria-label="Open Proof" onClick={t2} className={t3}>{t4}</Button></TooltipTrigger>;
    $[4] = t2;
    $[5] = tabIndex;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TooltipContent>Open Proof</TooltipContent>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5) {
    t7 = <Tooltip>{t5}{t6}</Tooltip>;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function PrCardMenu(t0) {
  const $ = _c(52);
  const {
    pr,
    onToggleAutofix,
    autofixLabel,
    onContinueAutofixSpendLimit,
    onContinueReviewRounds,
    onContinueProofRounds
  } = t0;
  const ready = isPrReady(pr);
  const ci = getPrCiStatus(pr);
  const {
    label: statusLabel,
    colorClass: statusColorClass
  } = getPrStatusLabel(pr.status, ready, ci);
  let t1;
  if ($[0] !== pr.created_at) {
    t1 = compactRelativeTime(pr.created_at);
    $[0] = pr.created_at;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const compactTime = t1;
  const showAutofix = pr.status !== "merged" && pr.status !== "closed";
  let t2;
  if ($[2] !== pr.description) {
    t2 = pr.description ? stripMarkdownForPreview(pr.description) : null;
    $[2] = pr.description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cleanDescription = t2;
  let t3;
  if ($[4] !== pr.repository) {
    t3 = pr.repository.split("/")[1] ?? pr.repository;
    $[4] = pr.repository;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const repoShort = t3;
  const {
    additions,
    deletions
  } = pr;
  const hasDiffStats = additions != null && deletions != null;
  const openProof = usePrProofOpener(pr);
  const t4 = pr.pr_url;
  let t5;
  if ($[6] !== pr.pr_number || $[7] !== repoShort) {
    t5 = <span className="min-w-0 truncate tabular-nums">{repoShort}#{pr.pr_number}</span>;
    $[6] = pr.pr_number;
    $[7] = repoShort;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== compactTime) {
    t6 = compactTime && <span className="shrink-0">{compactTime}</span>;
    $[9] = compactTime;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-2.5 text-xs font-normal leading-[18px] tracking-[-0.1px] text-[#727272] dark:text-neutral-500">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== autofixLabel || $[15] !== onToggleAutofix || $[16] !== pr || $[17] !== showAutofix) {
    t8 = showAutofix && <div className="flex shrink-0 items-center gap-2"><AutofixToggle pr={pr} onToggle={onToggleAutofix} tabIndex={-1} label={autofixLabel ?? "Autofix"} /></div>;
    $[14] = autofixLabel;
    $[15] = onToggleAutofix;
    $[16] = pr;
    $[17] = showAutofix;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <div className="flex items-center justify-between gap-2">{t7}{t8}</div>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = pr.title || `${repoShort}#${pr.pr_number}`;
  let t11;
  if ($[22] !== t10) {
    t11 = <HoverTickerText text={t10} className="type-body-s-regular text-[#18191A] dark:text-neutral-100" />;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== cleanDescription) {
    t12 = cleanDescription && <p className="mt-0.5 line-clamp-2 text-xs font-normal leading-[18px] tracking-[-0.1px] text-[#727272] dark:text-neutral-400">{cleanDescription}</p>;
    $[24] = cleanDescription;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = cn("truncate text-xs font-medium leading-[18px] tracking-[-0.1px]", statusColorClass);
  let t14;
  if ($[26] !== statusLabel || $[27] !== t13) {
    t14 = <span className={t13}>{statusLabel}</span>;
    $[26] = statusLabel;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== pr.active_autofix_batch) {
    t15 = pr.active_autofix_batch && <span title="Autofix in progress" className="flex shrink-0 items-center"><AutofixHammerIcon /></span>;
    $[29] = pr.active_autofix_batch;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== additions || $[32] !== deletions || $[33] !== hasDiffStats) {
    t16 = hasDiffStats && <BranchDiffStats additions={additions} deletions={deletions} className="text-xs leading-[18px] tracking-[-0.1px]" />;
    $[31] = additions;
    $[32] = deletions;
    $[33] = hasDiffStats;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== openProof) {
    t17 = openProof && <PrProofButton onOpen={openProof} />;
    $[35] = openProof;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== pr || $[38] !== showAutofix) {
    t18 = showAutofix && <PrPreviewLinksButton pr={pr} />;
    $[37] = pr;
    $[38] = showAutofix;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t17 || $[41] !== t18) {
    t19 = <div className="flex shrink-0 items-center gap-1.5 empty:hidden">{t17}{t18}</div>;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== onContinueReviewRounds || $[44] !== pr) {
    t20 = <PrRoundsBanner pr={pr} kind="review" onContinue={onContinueReviewRounds} className="mb-1.5" />;
    $[43] = onContinueReviewRounds;
    $[44] = pr;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== onContinueProofRounds || $[47] !== pr) {
    t21 = <PrRoundsBanner pr={pr} kind="proof" onContinue={onContinueProofRounds} className="mb-1.5" />;
    $[46] = onContinueProofRounds;
    $[47] = pr;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== onContinueAutofixSpendLimit || $[50] !== pr) {
    t22 = isAutofixSpendLimitPaused(pr) && <AutofixSpendLimitBanner pr={pr} onContinue={onContinueAutofixSpendLimit} className="mb-1.5" />;
    $[49] = onContinueAutofixSpendLimit;
    $[50] = pr;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  return <a href={t4} target="_blank" rel="noopener noreferrer" className="group group/pr flex flex-col rounded-md px-2 pt-1.5 no-underline cursor-pointer hover:bg-black/[0.04] dark:hover:bg-white/[0.04] focus:outline-none focus-visible:outline-none focus-visible:ring-0">{t9}{t11}{t12}<div className="mt-1 flex items-center justify-between gap-2 pb-1.5"><div className="flex min-w-0 items-center gap-2 overflow-hidden"><div className="flex min-w-0 items-center gap-1.5"><span className="flex shrink-0 items-center">{getPrStatusIcon(pr.status, ready, ci)}</span>{t14}{t15}</div>{t16}</div>{t19}</div>{t20}{t21}{t22}</a>;
}

/** Controls stay tabbable (tabIndex 0): the Summary tab is their primary surface. */
function PrCardSummary(t0) {
  const $ = _c(63);
  const {
    pr,
    onToggleAutofix,
    autofixLabel,
    onContinueAutofixSpendLimit,
    onContinueReviewRounds,
    onContinueProofRounds
  } = t0;
  const ready = isPrReady(pr);
  const ci = getPrCiStatus(pr);
  const {
    label: statusLabel,
    colorClass: statusColorClass
  } = getPrStatusLabel(pr.status, ready, ci);
  let t1;
  if ($[0] !== pr.created_at) {
    t1 = compactRelativeTime(pr.created_at);
    $[0] = pr.created_at;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const compactTime = t1;
  const showAutofix = pr.status !== "merged" && pr.status !== "closed";
  let t2;
  if ($[2] !== pr.repository) {
    t2 = pr.repository.split("/")[1] ?? pr.repository;
    $[2] = pr.repository;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const repoShort = t2;
  let t3;
  if ($[4] !== pr.description) {
    t3 = pr.description ? stripMarkdownForPreview(pr.description) : null;
    $[4] = pr.description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const cleanDescription = t3;
  const {
    additions,
    deletions
  } = pr;
  const hasDiffStats = additions != null && deletions != null;
  const openProof = usePrProofOpener(pr);
  const t4 = `Pull request #${pr.pr_number}`;
  const t5 = "group group/pr relative isolate -mx-2 flex min-w-0 flex-col gap-1 rounded-lg px-2 py-2 hover:bg-[var(--color-surface-inset)] focus-within:bg-[var(--color-surface-inset)]";
  let t6;
  if ($[6] !== pr.pr_number) {
    t6 = <span className="shrink-0 type-body-s-medium tabular-nums text-neutral-500 dark:text-neutral-400">#{pr.pr_number}</span>;
    $[6] = pr.pr_number;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = pr.title || `${repoShort}#${pr.pr_number}`;
  let t8;
  if ($[8] !== t7) {
    t8 = <HoverTickerText text={t7} className="min-w-0 flex-1 type-body-s-medium text-neutral-900 dark:text-neutral-100" />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== pr.pr_url || $[11] !== t6 || $[12] !== t8) {
    t9 = <a href={pr.pr_url} target="_blank" rel="noopener noreferrer" className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded no-underline after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-[var(--focus-border)]">{t6}{t8}</a>;
    $[10] = pr.pr_url;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== autofixLabel || $[15] !== onToggleAutofix || $[16] !== pr || $[17] !== showAutofix) {
    t10 = showAutofix && <div className="relative z-10 shrink-0"><AutofixToggle pr={pr} onToggle={onToggleAutofix} tabIndex={0} label={autofixLabel ?? "Autofix"} compact={true} /></div>;
    $[14] = autofixLabel;
    $[15] = onToggleAutofix;
    $[16] = pr;
    $[17] = showAutofix;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div className="flex h-[22px] min-w-0 items-center gap-1.5">{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = cleanDescription ? "text-neutral-500 dark:text-neutral-400" : "text-neutral-400 dark:text-neutral-500";
  let t13;
  if ($[22] !== t12) {
    t13 = cn("truncate type-body-xs-regular", t12);
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = cleanDescription || "No description";
  let t15;
  if ($[24] !== t13 || $[25] !== t14) {
    t15 = <div className="flex h-[22px] min-w-0 items-center"><p className={t13}>{t14}</p></div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = "flex h-[26px] min-w-0 items-center gap-1.5 overflow-hidden";
  const t17 = openProof ? <div className="relative z-10 flex shrink-0"><PrProofButton onOpen={openProof} tabIndex={0} /></div> : <span className={cn("inline-flex shrink-0 items-center gap-1 type-body-xs-medium", statusColorClass)}><span className="shrink-0">{getPrStatusIcon(pr.status, ready, ci)}</span><span>{statusLabel}</span></span>;
  let t18;
  if ($[27] !== pr || $[28] !== showAutofix) {
    t18 = showAutofix && <div className="relative z-10 flex shrink-0"><PrPreviewLinksButton pr={pr} tabIndex={0} /></div>;
    $[27] = pr;
    $[28] = showAutofix;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== pr.active_autofix_batch) {
    t19 = pr.active_autofix_batch && <span title="Autofix in progress" aria-label="Autofix in progress" className="inline-flex h-[22px] shrink-0 items-center"><AutofixHammerIcon size={14} /></span>;
    $[30] = pr.active_autofix_batch;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== additions || $[33] !== deletions || $[34] !== hasDiffStats) {
    t20 = hasDiffStats && <BranchDiffStats additions={additions} deletions={deletions} className="h-[22px]" />;
    $[32] = additions;
    $[33] = deletions;
    $[34] = hasDiffStats;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== compactTime) {
    t21 = compactTime && <span className="inline-flex h-[22px] shrink-0 items-center type-body-xs-regular tabular-nums text-neutral-500 dark:text-neutral-400">{compactTime}</span>;
    $[36] = compactTime;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t19 || $[39] !== t20 || $[40] !== t21) {
    t22 = <div className="flex h-[22px] min-w-0 flex-1 flex-wrap content-start items-center gap-x-1.5 overflow-hidden">{t19}{t20}{t21}</div>;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t17 || $[43] !== t18 || $[44] !== t22) {
    t23 = <div className={t16}>{t17}{t18}{t22}</div>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== onContinueReviewRounds || $[47] !== pr) {
    t24 = <PrRoundsBanner pr={pr} kind="review" onContinue={onContinueReviewRounds} className="relative z-10 mb-1.5 self-stretch" tabIndex={0} />;
    $[46] = onContinueReviewRounds;
    $[47] = pr;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== onContinueProofRounds || $[50] !== pr) {
    t25 = <PrRoundsBanner pr={pr} kind="proof" onContinue={onContinueProofRounds} className="relative z-10 mb-1.5 self-stretch" tabIndex={0} />;
    $[49] = onContinueProofRounds;
    $[50] = pr;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== onContinueAutofixSpendLimit || $[53] !== pr) {
    t26 = isAutofixSpendLimitPaused(pr) && <AutofixSpendLimitBanner pr={pr} onContinue={onContinueAutofixSpendLimit} className="relative z-10 self-stretch" tabIndex={0} />;
    $[52] = onContinueAutofixSpendLimit;
    $[53] = pr;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t11 || $[56] !== t15 || $[57] !== t23 || $[58] !== t24 || $[59] !== t25 || $[60] !== t26 || $[61] !== t4) {
    t27 = <article aria-label={t4} className={t5}>{t11}{t15}{t23}{t24}{t25}{t26}</article>;
    $[55] = t11;
    $[56] = t15;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
    $[61] = t4;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  return t27;
}
function PrCardChatHeader(t0) {
  const $ = _c(8);
  const {
    title,
    trailing
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <GitPullRequestArrow aria-hidden={true} className="size-3.5 shrink-0 text-[#18191a] dark:text-neutral-200" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <div className="flex min-w-0 items-center gap-2">{t1}<span className="pr-card-chat-title type-chat-prose truncate text-[#18191a] dark:text-neutral-100">{title}</span></div>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== trailing) {
    t3 = trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null;
    $[3] = trailing;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="flex items-center justify-between gap-2">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function BranchDiffStats(t0) {
  const $ = _c(15);
  const {
    additions,
    deletions,
    className
  } = t0;
  const t1 = `${additions} additions, ${deletions} deletions`;
  let t2;
  if ($[0] !== className) {
    t2 = cn("inline-flex shrink-0 items-center gap-1.5 text-xs leading-4", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== additions) {
    t3 = additions.toLocaleString("en-US");
    $[2] = additions;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="tabular-nums text-ds-green">+{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== deletions) {
    t5 = deletions.toLocaleString("en-US");
    $[6] = deletions;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="tabular-nums text-ds-red">-{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t4 || $[13] !== t6) {
    t7 = <span data-pr-diff-stats={true} aria-label={t1} className={t2}>{t4}{t6}</span>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function PrCardChat(t0) {
  const $ = _c(88);
  const {
    pr,
    onToggleAutofix,
    autofixLabel,
    onContinueAutofixSpendLimit,
    onContinueReviewRounds,
    onContinueProofRounds
  } = t0;
  let T0;
  let T1;
  let additions;
  let deletions;
  let hasDiffStats;
  let showAutofix;
  let statusLabel;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== pr) {
    const ready = isPrReady(pr);
    const ci = getPrCiStatus(pr);
    const {
      label: t14,
      colorClass: statusColorClass
    } = getPrStatusLabel(pr.status, ready, ci);
    statusLabel = t14;
    let t15;
    if ($[21] !== pr.description) {
      t15 = pr.description ? stripMarkdownForPreview(pr.description) : null;
      $[21] = pr.description;
      $[22] = t15;
    } else {
      t15 = $[22];
    }
    const cleanBody = t15;
    showAutofix = pr.status !== "merged" && pr.status !== "closed";
    ({
      additions,
      deletions
    } = pr);
    hasDiffStats = additions != null && deletions != null;
    let t16;
    if ($[23] !== pr.pr_number || $[24] !== pr.repository || $[25] !== pr.title) {
      t16 = pr.title || `${pr.repository.split("/")[1] ?? pr.repository}#${pr.pr_number}`;
      $[23] = pr.pr_number;
      $[24] = pr.repository;
      $[25] = pr.title;
      $[26] = t16;
    } else {
      t16 = $[26];
    }
    const title = t16;
    const hasUrl = !!pr.pr_url;
    const Wrapper = hasUrl ? "a" : "div";
    let t17;
    if ($[27] !== hasUrl || $[28] !== pr.pr_url) {
      t17 = hasUrl ? {
        href: pr.pr_url,
        target: "_blank",
        rel: "noopener noreferrer"
      } as const : {};
      $[27] = hasUrl;
      $[28] = pr.pr_url;
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    const wrapperProps = t17;
    const previewButton = getPreviewButtonState(pr) ? <PrPreviewButton pr={pr} iconClassName="size-2.5" gapClassName="gap-1.5" /> : null;
    t13 = "pt-1.5 pb-4";
    T1 = ChatCard;
    t11 = true;
    t12 = hasUrl;
    T0 = Wrapper;
    t6 = wrapperProps;
    t7 = true;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = cn(MESSAGE_INSET_FOCUS_BORDER_CLASS, "@container pr-card-chat group group/pr no-underline flex flex-col gap-1 focus:outline-none");
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    if ($[31] !== previewButton || $[32] !== title) {
      t9 = <PrCardChatHeader title={title} trailing={previewButton} />;
      $[31] = previewButton;
      $[32] = title;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    if ($[34] !== cleanBody) {
      t10 = cleanBody && <p className="type-body-s-regular text-[#727272] dark:text-neutral-400 line-clamp-2">{cleanBody}</p>;
      $[34] = cleanBody;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    t5 = "flex items-center justify-between gap-2 mt-0.5";
    t4 = "flex items-center gap-1.5 min-w-0";
    t2 = "flex items-center gap-1 shrink-0";
    t3 = getPrStatusIcon(pr.status, ready, ci);
    t1 = cn("text-xs font-medium leading-[18px] tracking-[-0.1px]", statusColorClass);
    $[0] = pr;
    $[1] = T0;
    $[2] = T1;
    $[3] = additions;
    $[4] = deletions;
    $[5] = hasDiffStats;
    $[6] = showAutofix;
    $[7] = statusLabel;
    $[8] = t1;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[1];
    T1 = $[2];
    additions = $[3];
    deletions = $[4];
    hasDiffStats = $[5];
    showAutofix = $[6];
    statusLabel = $[7];
    t1 = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t14;
  if ($[36] !== statusLabel || $[37] !== t1) {
    t14 = <span className={t1}>{statusLabel}</span>;
    $[36] = statusLabel;
    $[37] = t1;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14 || $[40] !== t2 || $[41] !== t3) {
    t15 = <div className={t2}>{t3}{t14}</div>;
    $[39] = t14;
    $[40] = t2;
    $[41] = t3;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== additions || $[44] !== deletions || $[45] !== hasDiffStats) {
    t16 = hasDiffStats && <BranchDiffStats additions={additions} deletions={deletions} />;
    $[43] = additions;
    $[44] = deletions;
    $[45] = hasDiffStats;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t15 || $[48] !== t16 || $[49] !== t4) {
    t17 = <div className={t4}>{t15}{t16}</div>;
    $[47] = t15;
    $[48] = t16;
    $[49] = t4;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== autofixLabel || $[52] !== onToggleAutofix || $[53] !== pr || $[54] !== showAutofix) {
    t18 = showAutofix && onToggleAutofix && <div className="flex shrink-0 items-center justify-end gap-2"><AutofixToggle pr={pr} onToggle={onToggleAutofix} tabIndex={-1} label={autofixLabel} /></div>;
    $[51] = autofixLabel;
    $[52] = onToggleAutofix;
    $[53] = pr;
    $[54] = showAutofix;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t17 || $[57] !== t18 || $[58] !== t5) {
    t19 = <div className={t5}>{t17}{t18}</div>;
    $[56] = t17;
    $[57] = t18;
    $[58] = t5;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== onContinueReviewRounds || $[61] !== pr) {
    t20 = <PrRoundsBanner pr={pr} kind="review" onContinue={onContinueReviewRounds} className="relative z-10 mb-1.5 self-stretch" tabIndex={0} />;
    $[60] = onContinueReviewRounds;
    $[61] = pr;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== onContinueProofRounds || $[64] !== pr) {
    t21 = <PrRoundsBanner pr={pr} kind="proof" onContinue={onContinueProofRounds} className="relative z-10 mb-1.5 self-stretch" tabIndex={0} />;
    $[63] = onContinueProofRounds;
    $[64] = pr;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== onContinueAutofixSpendLimit || $[67] !== pr) {
    t22 = isAutofixSpendLimitPaused(pr) && <AutofixSpendLimitBanner pr={pr} onContinue={onContinueAutofixSpendLimit} />;
    $[66] = onContinueAutofixSpendLimit;
    $[67] = pr;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== T0 || $[70] !== t10 || $[71] !== t19 || $[72] !== t20 || $[73] !== t21 || $[74] !== t22 || $[75] !== t6 || $[76] !== t7 || $[77] !== t8 || $[78] !== t9) {
    t23 = <T0 {...t6} data-pr-card-chat={t7} className={t8}>{t9}{t10}{t19}{t20}{t21}{t22}</T0>;
    $[69] = T0;
    $[70] = t10;
    $[71] = t19;
    $[72] = t20;
    $[73] = t21;
    $[74] = t22;
    $[75] = t6;
    $[76] = t7;
    $[77] = t8;
    $[78] = t9;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== T1 || $[81] !== t11 || $[82] !== t12 || $[83] !== t23) {
    t24 = <T1 asChild={t11} interactive={t12}>{t23}</T1>;
    $[80] = T1;
    $[81] = t11;
    $[82] = t12;
    $[83] = t23;
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  let t25;
  if ($[85] !== t13 || $[86] !== t24) {
    t25 = <div className={t13}>{t24}</div>;
    $[85] = t13;
    $[86] = t24;
    $[87] = t25;
  } else {
    t25 = $[87];
  }
  return t25;
}

// Shown in the new-session view before a session exists, when the user lands
// via "View in Codesmith".
interface PrCardPreviewProps {
  variant: "preview";
  pr: ManagedPr;
  onToggleAutofix?: (prId: string, enabled: boolean) => void | Promise<void>;
  onDismiss?: () => void;
}
function PrCardPreview(t0) {
  const $ = _c(27);
  const {
    pr,
    onToggleAutofix,
    onDismiss
  } = t0;
  const showAutofix = pr.status !== "merged" && pr.status !== "closed";
  let t1;
  if ($[0] !== pr.pr_number || $[1] !== pr.repository || $[2] !== pr.title) {
    t1 = pr.title || `${pr.repository.split("/")[1] ?? pr.repository}#${pr.pr_number}`;
    $[0] = pr.pr_number;
    $[1] = pr.repository;
    $[2] = pr.title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const title = t1;
  const hasUrl = !!pr.pr_url;
  const Wrapper = hasUrl ? "a" : "div";
  let t2;
  if ($[4] !== hasUrl || $[5] !== pr.pr_url) {
    t2 = hasUrl ? {
      href: pr.pr_url,
      target: "_blank",
      rel: "noopener noreferrer"
    } as const : {};
    $[4] = hasUrl;
    $[5] = pr.pr_url;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const wrapperProps = t2;
  const t3 = hasUrl && "cursor-pointer hover:bg-[var(--color-surface-inset-hover)]";
  let t4;
  if ($[7] !== t3) {
    t4 = cn("group/pr flex items-center gap-3 rounded-lg bg-[var(--color-surface-inset)] px-3 py-2.5 no-underline transition-colors duration-0 delay-0", t3);
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <GitPullRequestArrow className="h-[18px] w-[18px] text-[#18191a] dark:text-neutral-200 shrink-0" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== title) {
    t6 = <div className="flex items-center gap-1.5 min-w-0 flex-1">{t5}<span className="type-body-s-regular text-[#18191a] dark:text-neutral-100 truncate">{title}</span></div>;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onToggleAutofix || $[13] !== pr || $[14] !== showAutofix) {
    t7 = showAutofix && onToggleAutofix && <AutofixToggle pr={pr} onToggle={onToggleAutofix} tabIndex={-1} />;
    $[12] = onToggleAutofix;
    $[13] = pr;
    $[14] = showAutofix;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onDismiss) {
    t8 = onDismiss && <button onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    }} tabIndex={-1} className="flex items-center justify-center size-5 rounded-md cursor-pointer hover:bg-black/[0.06] dark:hover:bg-white/[0.06] focus:outline-none focus-visible:outline-none focus-visible:ring-0" aria-label="Dismiss PR card"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-[#727272] dark:text-neutral-400"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>;
    $[16] = onDismiss;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="flex items-center gap-3 shrink-0">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== Wrapper || $[22] !== t4 || $[23] !== t6 || $[24] !== t9 || $[25] !== wrapperProps) {
    t10 = <Wrapper {...wrapperProps} className={t4}>{t6}{t9}</Wrapper>;
    $[21] = Wrapper;
    $[22] = t4;
    $[23] = t6;
    $[24] = t9;
    $[25] = wrapperProps;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
export type PrCardProps = PrCardMenuProps | PrCardSummaryProps | PrCardChatProps | PrCardPreviewProps;
export function PrCard(props) {
  const $ = _c(25);
  if (props.variant === "preview") {
    let t0;
    if ($[0] !== props.onDismiss || $[1] !== props.onToggleAutofix || $[2] !== props.pr) {
      t0 = <PrCardPreview pr={props.pr} onToggleAutofix={props.onToggleAutofix} onDismiss={props.onDismiss} />;
      $[0] = props.onDismiss;
      $[1] = props.onToggleAutofix;
      $[2] = props.pr;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }
  if (props.variant === "summary") {
    let t0;
    if ($[4] !== props.autofixLabel || $[5] !== props.onContinueAutofixSpendLimit || $[6] !== props.onContinueProofRounds || $[7] !== props.onContinueReviewRounds || $[8] !== props.onToggleAutofix || $[9] !== props.pr) {
      t0 = <PrCardSummary pr={props.pr} onToggleAutofix={props.onToggleAutofix} autofixLabel={props.autofixLabel} onContinueAutofixSpendLimit={props.onContinueAutofixSpendLimit} onContinueReviewRounds={props.onContinueReviewRounds} onContinueProofRounds={props.onContinueProofRounds} />;
      $[4] = props.autofixLabel;
      $[5] = props.onContinueAutofixSpendLimit;
      $[6] = props.onContinueProofRounds;
      $[7] = props.onContinueReviewRounds;
      $[8] = props.onToggleAutofix;
      $[9] = props.pr;
      $[10] = t0;
    } else {
      t0 = $[10];
    }
    return t0;
  }
  if (props.variant === "chat") {
    let t0;
    if ($[11] !== props.autofixLabel || $[12] !== props.onContinueAutofixSpendLimit || $[13] !== props.onContinueProofRounds || $[14] !== props.onContinueReviewRounds || $[15] !== props.onToggleAutofix || $[16] !== props.pr) {
      t0 = <PrCardChat pr={props.pr} onToggleAutofix={props.onToggleAutofix} autofixLabel={props.autofixLabel} onContinueAutofixSpendLimit={props.onContinueAutofixSpendLimit} onContinueReviewRounds={props.onContinueReviewRounds} onContinueProofRounds={props.onContinueProofRounds} />;
      $[11] = props.autofixLabel;
      $[12] = props.onContinueAutofixSpendLimit;
      $[13] = props.onContinueProofRounds;
      $[14] = props.onContinueReviewRounds;
      $[15] = props.onToggleAutofix;
      $[16] = props.pr;
      $[17] = t0;
    } else {
      t0 = $[17];
    }
    return t0;
  }
  let t0;
  if ($[18] !== props.autofixLabel || $[19] !== props.onContinueAutofixSpendLimit || $[20] !== props.onContinueProofRounds || $[21] !== props.onContinueReviewRounds || $[22] !== props.onToggleAutofix || $[23] !== props.pr) {
    t0 = <PrCardMenu pr={props.pr} onToggleAutofix={props.onToggleAutofix} autofixLabel={props.autofixLabel} onContinueAutofixSpendLimit={props.onContinueAutofixSpendLimit} onContinueReviewRounds={props.onContinueReviewRounds} onContinueProofRounds={props.onContinueProofRounds} />;
    $[18] = props.autofixLabel;
    $[19] = props.onContinueAutofixSpendLimit;
    $[20] = props.onContinueProofRounds;
    $[21] = props.onContinueReviewRounds;
    $[22] = props.onToggleAutofix;
    $[23] = props.pr;
    $[24] = t0;
  } else {
    t0 = $[24];
  }
  return t0;
}